<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import Draggable from 'vuedraggable';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const router = useRouter();
const route = useRoute();

const validViews = ['overview', 'leads', 'recovery', 'closures'];
const activeView = ref(
  validViews.includes(route.query.view) ? route.query.view : 'overview'
);
const activeStatus = ref('todos');
const activeLeadPeriod = ref('todos');
const searchQuery = ref('');
const leads = ref([]);
const currentPage = ref(1);
const totalCount = ref(0);

const closingDialogRef = ref(null);
const pendingClosingContact = ref(null);
const notClosedDialogRef = ref(null);
const pendingNotClosedContact = ref(null);
const notClosedReason = ref('');
const scheduleNotClosedRecovery = ref(false);
const notClosedRecoveryMode = ref('30');
const customNotClosedRecoveryDate = ref('');
const listSelectedStatuses = ref({});
const closingMode = ref('30');
const customClosingDate = ref('');
const isLoading = ref(false);
const errorMessage = ref('');
const updatingContactId = ref(null);
const openingConversationContactId = ref(null);
const leadsDisplayMode = ref('list');
const kanbanLeads = ref([]);
const kanbanLists = ref({
  novo: [],
  em_atendimento: [],
  aguardando: [],
  nao_fechou: [],
  fechado: [],
});
const isKanbanLoading = ref(false);

const recoveryPeriod = ref('todos');
const recoveryType = ref('todos');
const recoverySort = ref('date_asc');
const recoveryContacts = ref([]);
const recoveryCurrentPage = ref(1);
const recoveryTotalCount = ref(0);
const isRecoveryLoading = ref(false);

const todayForClosures = new Date();
const closuresMonth = ref(
  `${todayForClosures.getFullYear()}-${String(
    todayForClosures.getMonth() + 1
  ).padStart(2, '0')}`
);
const closuresSort = ref('date_desc');
const closuresContacts = ref([]);
const closuresCurrentPage = ref(1);
const closuresTotalCount = ref(0);
const closuresByResponsible = ref({});
const isClosuresLoading = ref(false);
const isExportingClosures = ref(false);
const closuresErrorMessage = ref('');

const overviewData = ref({
  leads: {
    hoje: 0,
    semana: 0,
    mes: 0,
  },
  fechados: 0,
  funil: {
    novo: 0,
    em_atendimento: 0,
    aguardando: 0,
    nao_fechou: 0,
    fechado: 0,
  },
  repescagem: {
    hoje: 0,
    atrasados: 0,
  },
});
const isOverviewLoading = ref(false);
const overviewErrorMessage = ref('');

const STATUS_KEY = 'mayara_status_comercial';
const ENTRY_KEY = 'mayara_data_entrada_lead';
const ORIGIN_KEY = 'mayara_origem_lead';
const OWNER_KEY = 'mayara_responsavel_comercial';
const CLOSING_KEY = 'mayara_data_fechamento';

const views = [
  { key: 'overview', label: 'Visão geral', icon: 'i-lucide-layout-dashboard' },
  { key: 'leads', label: 'Leads', icon: 'i-lucide-users' },
  { key: 'recovery', label: 'Repescagem', icon: 'i-lucide-refresh-cw' },
  {
    key: 'closures',
    label: 'Fechamentos',
    icon: 'i-lucide-badge-check',
  },
];

const statusOptions = [
  { value: 'todos', label: 'Todos' },
  { value: 'novo', label: 'Novos' },
  { value: 'em_atendimento', label: 'Em atendimento' },
  { value: 'aguardando', label: 'Aguardando' },
  { value: 'nao_fechou', label: 'Não fechou' },
  { value: 'fechado', label: 'Fechados' },
];

const editableStatuses = statusOptions.filter(item => item.value !== 'todos');

const recoveryPeriods = [
  { value: 'todos', label: 'Todos' },
  { value: 'atrasados', label: 'Atrasados' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'proximos_7', label: 'Próximos 7 dias' },
  { value: 'futuros', label: 'Futuras' },
];

const recoveryTypes = [
  { value: 'todos', label: 'Todos os tipos' },
  { value: 'nao_fechou', label: 'Não fechou' },
  { value: 'renovacao', label: 'Renovação' },
  { value: 'reativacao', label: 'Reativação' },
  { value: 'outro', label: 'Outro' },
];

const recoverySortOptions = [
  { value: 'date_asc', label: 'Mais próximas' },
  { value: 'date_desc', label: 'Mais distantes' },
  { value: 'type', label: 'Tipo' },
];

const closuresSortOptions = [
  { value: 'date_desc', label: 'Mais recentes' },
  { value: 'date_asc', label: 'Mais antigos' },
  { value: 'responsible', label: 'Responsável' },
];

const recoveryTypeLabel = type => {
  const labels = {
    nao_fechou: 'Não fechou',
    renovacao: 'Renovação',
    reativacao: 'Reativação',
    outro: 'Outro',
  };

  return labels[type] || 'Nova abordagem';
};

const recoveryTypeClass = type => {
  const classes = {
    nao_fechou: 'bg-n-ruby-3 text-n-ruby-11',
    renovacao: 'bg-n-green-3 text-n-green-11',
    reativacao: 'bg-n-blue-3 text-n-blue-11',
    outro: 'bg-n-alpha-2 text-n-slate-10',
  };

  return classes[type] || 'bg-n-alpha-2 text-n-slate-10';
};

const kanbanVisuals = {
  novo: {
    icon: 'i-lucide-sparkles',
    accent: 'bg-n-blue-9',
    soft: 'bg-n-blue-3',
    text: 'text-n-blue-11',
  },
  em_atendimento: {
    icon: 'i-lucide-messages-square',
    accent: 'bg-n-teal-9',
    soft: 'bg-n-teal-3',
    text: 'text-n-teal-11',
  },
  aguardando: {
    icon: 'i-lucide-clock-3',
    accent: 'bg-n-amber-9',
    soft: 'bg-n-amber-3',
    text: 'text-n-amber-11',
  },
  nao_fechou: {
    icon: 'i-lucide-circle-x',
    accent: 'bg-n-ruby-9',
    soft: 'bg-n-ruby-3',
    text: 'text-n-ruby-11',
  },
  fechado: {
    icon: 'i-lucide-circle-check',
    accent: 'bg-n-green-9',
    soft: 'bg-n-green-3',
    text: 'text-n-green-11',
  },
};

const kanbanColumns = editableStatuses.map(status => ({
  key: status.value,
  label: status.label,
  ...kanbanVisuals[status.value],
}));

const metrics = computed(() => [
  {
    label: 'Leads hoje',
    value: overviewData.value.leads.hoje,
    description: 'Entraram no funil hoje',
    icon: 'i-lucide-user-plus',
    action: 'leads',
    period: 'hoje',
  },
  {
    label: 'Esta semana',
    value: overviewData.value.leads.semana,
    description: 'Novos leads na semana',
    icon: 'i-lucide-calendar-days',
    action: 'leads',
    period: 'semana',
  },
  {
    label: 'Este mês',
    value: overviewData.value.leads.mes,
    description: 'Novos leads no mês',
    icon: 'i-lucide-calendar-range',
    action: 'leads',
    period: 'mes',
  },
  {
    label: 'Fechados no mês',
    value: overviewData.value.fechados,
    description: 'Conversões comerciais neste mês',
    icon: 'i-lucide-circle-check',
    action: 'closures',
  },
]);

const funnel = computed(() => [
  {
    key: 'novo',
    title: 'Novos leads',
    value: overviewData.value.funil.novo,
    description: 'Pessoas que acabaram de entrar no comercial.',
    icon: 'i-lucide-sparkles',
  },
  {
    key: 'em_atendimento',
    title: 'Em atendimento',
    value: overviewData.value.funil.em_atendimento,
    description: 'Leads que estão conversando com a equipe.',
    icon: 'i-lucide-messages-square',
  },
  {
    key: 'aguardando',
    title: 'Aguardando',
    value: overviewData.value.funil.aguardando,
    description: 'Leads que precisam de retorno ou decisão.',
    icon: 'i-lucide-clock-3',
  },
  {
    key: 'nao_fechou',
    title: 'Não fechou',
    value: overviewData.value.funil.nao_fechou,
    description: 'Oportunidades que não converteram neste momento.',
    icon: 'i-lucide-circle-x',
  },
  {
    key: 'fechado',
    title: 'Fechados',
    value: overviewData.value.funil.fechado,
    description: 'Leads atualmente classificados como fechados.',
    icon: 'i-lucide-circle-check',
  },
]);

const statusLabel = status =>
  editableStatuses.find(item => item.value === status)?.label || status || '—';

const statusClass = status => {
  const classes = {
    novo: 'bg-n-blue-3 text-n-blue-11',
    em_atendimento: 'bg-n-teal-3 text-n-teal-11',
    aguardando: 'bg-n-amber-3 text-n-amber-11',
    nao_fechou: 'bg-n-ruby-3 text-n-ruby-11',
    fechado: 'bg-n-green-3 text-n-green-11',
  };
  return classes[status] || 'bg-n-alpha-2 text-n-slate-10';
};

const originLabel = origin => {
  const labels = {
    instagram: 'Instagram',
    whatsapp: 'WhatsApp',
    indicacao: 'Indicação',
    google: 'Google',
    outro: 'Outro',
  };
  return labels[origin] || 'Origem não informada';
};

const formatDate = value => {
  if (!value) return 'Data não informada';

  const raw = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T12:00:00`)
    : new Date(raw);

  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const contactDetail = contact =>
  contact.phone_number || contact.email || 'Contato sem telefone/e-mail';

const contactInitials = contact =>
  (contact.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const filteredLeads = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return leads.value;

  return leads.value.filter(contact =>
    [contact.name, contact.phone_number, contact.email]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query))
  );
});

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / 15)));

const recoveryTotalPages = computed(() =>
  Math.max(1, Math.ceil(recoveryTotalCount.value / 15))
);

const closuresTotalPages = computed(() =>
  Math.max(1, Math.ceil(closuresTotalCount.value / 15))
);

const closuresMonthLabel = computed(() => {
  const [year, month] = closuresMonth.value.split('-').map(Number);
  if (!year || !month) return closuresMonth.value;

  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, 1));

  return label.charAt(0).toUpperCase() + label.slice(1);
});

const closuresResponsibleCards = computed(() =>
  Object.entries(closuresByResponsible.value)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'pt-BR'))
);

const changeClosuresMonth = offset => {
  const [year, month] = closuresMonth.value.split('-').map(Number);
  const date = new Date(year, month - 1 + offset, 1);

  closuresMonth.value = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, '0')}`;
};

const fetchClosures = async (page = 1) => {
  isClosuresLoading.value = true;
  closuresErrorMessage.value = '';

  try {
    const response = await ContactAPI.closures(
      page,
      closuresMonth.value,
      closuresSort.value
    );

    closuresContacts.value = response.data.payload || [];
    closuresTotalCount.value = response.data.meta?.count || 0;
    closuresByResponsible.value = response.data.meta?.by_responsible || {};
    closuresCurrentPage.value = page;
  } catch (error) {
    closuresContacts.value = [];
    closuresTotalCount.value = 0;
    closuresByResponsible.value = {};
    closuresErrorMessage.value =
      'Não foi possível carregar o relatório de fechamentos.';
  } finally {
    isClosuresLoading.value = false;
  }
};

const csvEscape = value => {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
};

const exportClosuresCsv = async () => {
  if (!closuresTotalCount.value || isExportingClosures.value) return;

  isExportingClosures.value = true;
  closuresErrorMessage.value = '';

  try {
    const perPage = 100;
    const pages = Math.ceil(closuresTotalCount.value / perPage);
    const contacts = [];

    for (let page = 1; page <= pages; page += 1) {
      const response = await ContactAPI.closures(
        page,
        closuresMonth.value,
        closuresSort.value,
        perPage
      );
      contacts.push(...(response.data.payload || []));
    }

    const rows = [
      ['Contato', 'Data do fechamento', 'Responsável', 'Origem', 'Telefone', 'E-mail'],
      ...contacts.map(contact => [
        contact.name || 'Contato sem nome',
        contact.closure_event?.occurred_on || '',
        contact.closure_event?.responsible || 'Não informado',
        originLabel(contact.closure_event?.origin),
        contact.phone_number || '',
        contact.email || '',
      ]),
    ];

    const csv = rows
      .map(row => row.map(csvEscape).join(';'))
      .join('\r\n');

    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `mayara-care-fechamentos-${closuresMonth.value}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    closuresErrorMessage.value =
      'Não foi possível exportar o relatório de fechamentos.';
  } finally {
    isExportingClosures.value = false;
  }
};

const fetchOverview = async () => {
  isOverviewLoading.value = true;
  overviewErrorMessage.value = '';

  try {
    const response = await ContactAPI.overview();
    const data = response.data || {};

    overviewData.value = {
      leads: {
        hoje: data.leads?.hoje ?? 0,
        semana: data.leads?.semana ?? 0,
        mes: data.leads?.mes ?? 0,
      },
      fechados: data.fechados ?? 0,
      funil: {
        novo: data.funil?.novo ?? 0,
        em_atendimento: data.funil?.em_atendimento ?? 0,
        aguardando: data.funil?.aguardando ?? 0,
        nao_fechou: data.funil?.nao_fechou ?? 0,
        fechado: data.funil?.fechado ?? 0,
      },
      repescagem: {
        hoje: data.repescagem?.hoje ?? 0,
        atrasados: data.repescagem?.atrasados ?? 0,
      },
    };
  } catch (error) {
    overviewErrorMessage.value =
      'Não foi possível carregar os indicadores comerciais.';
  } finally {
    isOverviewLoading.value = false;
  }
};

const openRecoveryPeriod = period => {
  recoveryPeriod.value = period;
  activeView.value = 'recovery';
};

const openLeadsStatus = status => {
  activeStatus.value = status;
  activeLeadPeriod.value = 'todos';
  leadsDisplayMode.value = 'list';
  activeView.value = 'leads';
};

const openLeadsPeriod = period => {
  activeStatus.value = 'todos';
  activeLeadPeriod.value = period;
  leadsDisplayMode.value = 'list';
  activeView.value = 'leads';
};

const openCurrentMonthClosures = () => {
  const now = new Date();
  closuresMonth.value = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}`;
  activeView.value = 'closures';
};

const backToOverview = () => {
  activeView.value = 'overview';
};

const startingRenewalContactId = ref(null);

const canStartRenewal = contact => {
  const attributes = contact?.custom_attributes || {};
  const recoveryDate = attributes.mayara_proxima_repescagem;

  return (
    attributes[STATUS_KEY] === 'fechado' &&
    attributes.mayara_tipo_repescagem === 'renovacao' &&
    Boolean(recoveryDate) &&
    recoveryDate <= today()
  );
};

const startRenewal = async contact => {
  if (
    !contact?.id ||
    !canStartRenewal(contact) ||
    startingRenewalContactId.value
  ) {
    return;
  }

  startingRenewalContactId.value = contact.id;
  errorMessage.value = '';

  try {
    await ContactAPI.startRenewal(contact.id);
    await fetchRecovery(recoveryCurrentPage.value);
  } catch (error) {
    errorMessage.value =
      'Não foi possível iniciar a renovação deste contato.';
  } finally {
    startingRenewalContactId.value = null;
  }
};

const fetchRecovery = async (page = 1) => {
  isRecoveryLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await ContactAPI.recovery(
      page,
      recoveryPeriod.value,
      recoveryType.value,
      recoverySort.value
    );

    recoveryContacts.value = response.data.payload || [];
    recoveryTotalCount.value = response.data.meta?.count || 0;
    recoveryCurrentPage.value = page;
  } catch (error) {
    recoveryContacts.value = [];
    recoveryTotalCount.value = 0;
    errorMessage.value =
      'Não foi possível carregar a fila de repescagem. Tente novamente.';
  } finally {
    isRecoveryLoading.value = false;
  }
};

const fetchLeads = async (page = 1) => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await ContactAPI.commercial(
      page,
      '-last_activity_at',
      activeStatus.value,
      activeLeadPeriod.value
    );

    leads.value = response.data.payload || [];
    totalCount.value = response.data.meta?.count || 0;
    currentPage.value = page;
  } catch (error) {
    leads.value = [];
    totalCount.value = 0;
    errorMessage.value =
      'Não foi possível carregar os leads. Tente novamente.';
  } finally {
    isLoading.value = false;
  }
};

const fetchKanbanLeads = async () => {
  isKanbanLoading.value = true;
  errorMessage.value = '';

  try {
    const firstResponse = await ContactAPI.commercial(
      1,
      '-last_activity_at',
      'todos'
    );

    const firstPage = firstResponse.data.payload || [];
    const count = firstResponse.data.meta?.count || 0;
    const pages = Math.max(1, Math.ceil(count / 15));
    const allLeads = [...firstPage];

    for (let page = 2; page <= pages; page += 1) {
      const response = await ContactAPI.commercial(
        page,
        '-last_activity_at',
        'todos'
      );
      allLeads.push(...(response.data.payload || []));
    }

    kanbanLeads.value = allLeads;

    kanbanLists.value = Object.fromEntries(
      kanbanColumns.map(column => [
        column.key,
        allLeads.filter(
          contact =>
            contact.custom_attributes?.[STATUS_KEY] === column.key
        ),
      ])
    );
  } catch (error) {
    kanbanLeads.value = [];
    kanbanLists.value = {
      novo: [],
      em_atendimento: [],
      aguardando: [],
      nao_fechou: [],
      fechado: [],
    };
    errorMessage.value =
      'Não foi possível carregar o Kanban comercial.';
  } finally {
    isKanbanLoading.value = false;
  }
};

const closingOptions = [
  { value: '30', label: '30 dias' },
  { value: '45', label: '45 dias' },
  { value: '60', label: '60 dias' },
  { value: '90', label: '90 dias' },
  { value: 'custom', label: 'Escolher data' },
];

const notClosedRecoveryOptions = [
  { value: '7', label: '7 dias' },
  { value: '15', label: '15 dias' },
  { value: '30', label: '30 dias' },
  { value: '60', label: '60 dias' },
  { value: 'custom', label: 'Escolher data' },
];

const localDateString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const today = () => localDateString(new Date());

const addDays = days => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + Number(days));
  return localDateString(date);
};

const closingNextContactDate = computed(() => {
  if (closingMode.value === 'custom') {
    return customClosingDate.value;
  }

  return addDays(closingMode.value);
});

const closingDateIsInvalid = computed(() => {
  if (!closingNextContactDate.value) return true;
  return closingNextContactDate.value <= today();
});

const formattedClosingDate = computed(() => {
  if (!closingNextContactDate.value) return '';

  const [year, month, day] = closingNextContactDate.value
    .split('-')
    .map(Number);

  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(year, month - 1, day, 12, 0, 0)
  );
});

const notClosedRecoveryDate = computed(() => {
  if (!scheduleNotClosedRecovery.value) return '';

  if (notClosedRecoveryMode.value === 'custom') {
    return customNotClosedRecoveryDate.value;
  }

  return addDays(notClosedRecoveryMode.value);
});

const notClosedRecoveryDateIsInvalid = computed(() => {
  if (!scheduleNotClosedRecovery.value) return false;
  if (!notClosedRecoveryDate.value) return true;
  return notClosedRecoveryDate.value <= today();
});

const notClosedReasonIsInvalid = computed(
  () => !notClosedReason.value.trim()
);

const notClosedFormIsInvalid = computed(
  () =>
    notClosedReasonIsInvalid.value ||
    notClosedRecoveryDateIsInvalid.value
);

const formattedNotClosedRecoveryDate = computed(() => {
  if (!notClosedRecoveryDate.value) return '';

  const [year, month, day] = notClosedRecoveryDate.value
    .split('-')
    .map(Number);

  return new Intl.DateTimeFormat('pt-BR').format(
    new Date(year, month - 1, day, 12, 0, 0)
  );
});

const openNotClosedDialog = contact => {
  pendingNotClosedContact.value = contact;
  notClosedReason.value = '';
  scheduleNotClosedRecovery.value = false;
  notClosedRecoveryMode.value = '30';
  customNotClosedRecoveryDate.value = '';
  notClosedDialogRef.value?.open();
};

const clearPendingNotClosed = () => {
  pendingNotClosedContact.value = null;
  notClosedReason.value = '';
  scheduleNotClosedRecovery.value = false;
  notClosedRecoveryMode.value = '30';
  customNotClosedRecoveryDate.value = '';
};

const openClosingDialog = contact => {
  pendingClosingContact.value = contact;
  closingMode.value = '30';
  customClosingDate.value = '';
  closingDialogRef.value?.open();
};

const clearPendingClosing = () => {
  pendingClosingContact.value = null;
  closingMode.value = '30';
  customClosingDate.value = '';
};

const getListSelectedStatus = contact =>
  listSelectedStatuses.value[contact.id] ??
  contact.custom_attributes?.[STATUS_KEY] ??
  '';

const clearListSelectedStatus = contact => {
  if (contact?.id) {
    delete listSelectedStatuses.value[contact.id];
  }
};

const handleListStatusChange = async (contact, newStatus) => {
  const persistedStatus = contact.custom_attributes?.[STATUS_KEY] || '';

  if (newStatus === 'fechado') {
    listSelectedStatuses.value[contact.id] = persistedStatus;
    setTimeout(() => openClosingDialog(contact), 0);
    return;
  }

  if (newStatus === 'nao_fechou') {
    listSelectedStatuses.value[contact.id] = persistedStatus;
    setTimeout(() => openNotClosedDialog(contact), 0);
    return;
  }

  await updateStatus(contact, newStatus);
  clearListSelectedStatus(contact);
};

const updateStatus = async (contact, newStatus) => {
  if (!newStatus || newStatus === contact.custom_attributes?.[STATUS_KEY]) {
    return false;
  }

  if (newStatus === 'fechado') {
    openClosingDialog(contact);
    return false;
  }

  if (newStatus === 'nao_fechou') {
    openNotClosedDialog(contact);
    return false;
  }

  updatingContactId.value = contact.id;
  errorMessage.value = '';

  try {
    const customAttributes = {
      ...(contact.custom_attributes || {}),
      [STATUS_KEY]: newStatus,
    };

    await ContactAPI.update(contact.id, {
      custom_attributes: customAttributes,
    });

    await fetchLeads(currentPage.value);
    return true;
  } catch (error) {
    errorMessage.value =
      'Não foi possível alterar o status deste lead.';
    return false;
  } finally {
    updatingContactId.value = null;
  }
};

const confirmClosing = async () => {
  const contact = pendingClosingContact.value;

  if (!contact || closingDateIsInvalid.value) return;

  updatingContactId.value = contact.id;
  errorMessage.value = '';

  try {
    await ContactAPI.closeCommercial(
      contact.id,
      closingNextContactDate.value
    );

    closingDialogRef.value?.close();

    await fetchLeads(currentPage.value);

    if (leadsDisplayMode.value === 'kanban') {
      await fetchKanbanLeads();
    }

    clearListSelectedStatus(contact);
    clearPendingClosing();
  } catch (error) {
    errorMessage.value =
      'Não foi possível concluir o fechamento deste lead.';
  } finally {
    updatingContactId.value = null;
  }
};

const handleClosingDialogClose = () => {
  clearListSelectedStatus(pendingClosingContact.value);
  clearPendingClosing();
};

const confirmNotClosed = async () => {
  const contact = pendingNotClosedContact.value;

  if (!contact || notClosedFormIsInvalid.value) return;

  updatingContactId.value = contact.id;
  errorMessage.value = '';

  try {
    const customAttributes = {
      ...(contact.custom_attributes || {}),
      [STATUS_KEY]: 'nao_fechou',
      mayara_motivo_nao_fechamento: notClosedReason.value.trim(),
    };

    if (scheduleNotClosedRecovery.value) {
      customAttributes.mayara_proxima_repescagem =
        notClosedRecoveryDate.value;
      customAttributes.mayara_tipo_repescagem = 'nao_fechou';
    } else {
      delete customAttributes.mayara_proxima_repescagem;
      delete customAttributes.mayara_tipo_repescagem;
    }

    await ContactAPI.update(contact.id, {
      custom_attributes: customAttributes,
    });

    notClosedDialogRef.value?.close();

    await fetchLeads(currentPage.value);

    if (leadsDisplayMode.value === 'kanban') {
      await fetchKanbanLeads();
    }

    clearListSelectedStatus(contact);
    clearPendingNotClosed();
  } catch (error) {
    errorMessage.value =
      'Não foi possível marcar este lead como não fechado.';
  } finally {
    updatingContactId.value = null;
  }
};

const handleNotClosedDialogClose = () => {
  clearListSelectedStatus(pendingNotClosedContact.value);
  clearPendingNotClosed();
};

const handleKanbanChange = async (status, event) => {
  const contact = event?.added?.element;
  if (!contact) return;

  const previousStatus = contact.custom_attributes?.[STATUS_KEY];
  if (previousStatus === status) return;

  if (status === 'fechado') {
    openClosingDialog(contact);

    // O draggable já moveu o card localmente. Recarregamos o Kanban
    // para manter a UI fiel ao estado persistido enquanto o fechamento
    // aguarda confirmação.
    await fetchKanbanLeads();
    return;
  }

  if (status === 'nao_fechou') {
    openNotClosedDialog(contact);

    // O draggable já moveu o card localmente. Recarregamos o Kanban
    // para manter a UI fiel ao estado persistido enquanto a decisão
    // de não fechamento aguarda confirmação.
    await fetchKanbanLeads();
    return;
  }

  await updateStatus(contact, status);
  await fetchKanbanLeads();
};

const openContact = contact => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: contact.id,
    },
    query: {
      from: 'commercial',
      view: 'leads',
    },
  });
};

const openLatestConversation = async contact => {
  if (openingConversationContactId.value) return;

  openingConversationContactId.value = contact.id;
  errorMessage.value = '';

  try {
    const response = await ContactAPI.getConversations(contact.id);
    const conversations = response.data.payload || [];
    const latestConversation = conversations[0];

    if (!latestConversation) {
      errorMessage.value =
        `Nenhuma conversa encontrada para ${contact.name || 'este contato'}.`;
      return;
    }

    await router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: latestConversation.display_id || latestConversation.id,
      },
    });
  } catch (error) {
    errorMessage.value =
      'Não foi possível abrir a última conversa deste lead.';
  } finally {
    openingConversationContactId.value = null;
  }
};

const goToPage = page => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  fetchLeads(page);
};

const goToRecoveryPage = page => {
  if (
    page < 1 ||
    page > recoveryTotalPages.value ||
    page === recoveryCurrentPage.value
  ) {
    return;
  }

  fetchRecovery(page);
};

const goToClosuresPage = page => {
  if (
    page < 1 ||
    page > closuresTotalPages.value ||
    page === closuresCurrentPage.value
  ) {
    return;
  }

  fetchClosures(page);
};

watch([closuresMonth, closuresSort], () => {
  if (activeView.value === 'closures') {
    fetchClosures(1);
  }
});

watch([recoveryPeriod, recoveryType, recoverySort], () => {
  if (activeView.value === 'recovery') {
    fetchRecovery(1);
  }
});

watch([activeStatus, activeLeadPeriod], () => {
  if (activeView.value === 'leads' && leadsDisplayMode.value === 'list') {
    fetchLeads(1);
  }
});

watch(leadsDisplayMode, mode => {
  if (activeView.value !== 'leads') return;

  if (mode === 'kanban') {
    fetchKanbanLeads();
  } else {
    fetchLeads(1);
  }
});

watch(activeView, view => {
  if (view === 'overview') fetchOverview();
  if (view === 'leads') fetchLeads(1);
  if (view === 'recovery') fetchRecovery(1);
  if (view === 'closures') fetchClosures(1);

  const currentQueryView = route.query.view;
  if (currentQueryView !== view) {
    router.replace({
      name: 'mayara_commercial',
      params: { accountId: route.params.accountId },
      query: { ...route.query, view },
    });
  }
});

watch(
  () => route.query.view,
  view => {
    if (validViews.includes(view) && activeView.value !== view) {
      activeView.value = view;
    }
  }
);

onMounted(() => {
  if (activeView.value === 'overview') fetchOverview();
  if (activeView.value === 'leads') fetchLeads(1);
  if (activeView.value === 'recovery') fetchRecovery(1);
  if (activeView.value === 'closures') fetchClosures(1);
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-[1500px] px-8 py-8 mx-auto">
      <div
        class="flex flex-col gap-5 pb-6 border-b border-n-weak lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <div
            class="text-[11px] font-semibold uppercase tracking-[0.18em] text-n-slate-8"
          >
            Mayara Care
          </div>

          <h1 class="mt-1 text-3xl font-semibold tracking-tight text-n-slate-12">
            Comercial
          </h1>

          <p class="max-w-2xl mt-2 text-sm leading-6 text-n-slate-10">
            Acompanhe os leads desde a entrada no funil até o fechamento
            e organize as próximas oportunidades da equipe.
          </p>
        </div>

        <div
          class="inline-flex self-start p-1 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <button
            v-for="view in views"
            :key="view.key"
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all rounded-lg"
            :class="
              activeView === view.key
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="activeView = view.key"
          >
            <i :class="[view.icon, 'text-base']" />
            {{ view.label }}
          </button>
        </div>
      </div>

      <template v-if="activeView === 'overview'">
        <div
          v-if="overviewErrorMessage"
          class="px-4 py-3 mt-6 text-sm border rounded-xl border-n-ruby-6 text-n-ruby-11 bg-n-ruby-2"
        >
          {{ overviewErrorMessage }}
        </div>

        <div class="grid gap-4 mt-6 md:grid-cols-2 xl:grid-cols-4">
          <button
            v-for="metric in metrics"
            :key="metric.label"
            type="button"
            class="relative overflow-hidden p-5 text-left transition-all border rounded-2xl border-n-weak bg-n-solid-2 before:absolute before:inset-x-0 before:top-0 before:h-1"
            :class="[
              metric.action
                ? 'cursor-pointer hover:bg-n-alpha-1 hover:border-n-strong hover:shadow-sm'
                : 'cursor-default',
              metric.period === 'hoje'
                ? 'before:bg-n-blue-8'
                : metric.period === 'semana'
                  ? 'before:bg-n-teal-8'
                  : metric.period === 'mes'
                    ? 'before:bg-n-amber-8'
                    : 'before:bg-n-green-8',
            ]"
            @click="
              metric.action === 'closures'
                ? openCurrentMonthClosures()
                : metric.action === 'leads'
                  ? openLeadsPeriod(metric.period)
                  : null
            "
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <div class="text-sm font-medium text-n-slate-10">
                  {{ metric.label }}
                </div>
                <div class="mt-2 text-3xl font-semibold text-n-slate-12">
                  <i
                    v-if="isOverviewLoading"
                    class="text-xl i-lucide-loader-circle animate-spin text-n-slate-8"
                  />
                  <template v-else>
                    {{ metric.value }}
                  </template>
                </div>
              </div>

              <span
                class="flex items-center justify-center w-10 h-10 rounded-xl"
                :class="
                  metric.period === 'hoje'
                    ? 'bg-n-blue-3 text-n-blue-11'
                    : metric.period === 'semana'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : metric.period === 'mes'
                        ? 'bg-n-amber-3 text-n-amber-11'
                        : 'bg-n-green-3 text-n-green-11'
                "
              >
                <i :class="[metric.icon, 'text-lg']" />
              </span>
            </div>

            <div class="flex items-center justify-between gap-3 mt-3 text-xs text-n-slate-8">
              <span>{{ metric.description }}</span>
              <i
                v-if="metric.action"
                class="i-lucide-arrow-up-right text-n-slate-7"
              />
            </div>
          </button>
        </div>

        <div class="grid gap-6 mt-6 xl:grid-cols-[1.4fr_0.8fr]">
          <section class="p-6 border rounded-2xl border-n-weak bg-n-solid-2">
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                Funil comercial
              </h2>
              <p class="mt-1 text-sm text-n-slate-9">
                Uma visão simples do momento de cada lead.
              </p>
            </div>

            <div class="grid gap-3 mt-5 md:grid-cols-2">
              <button
                v-for="item in funnel"
                :key="item.title"
                type="button"
                class="relative flex gap-4 p-4 overflow-hidden text-left transition-all border rounded-xl border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 hover:border-n-strong hover:shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-1"
                :class="
                  item.key === 'novo'
                    ? 'before:bg-n-blue-8'
                    : item.key === 'em_atendimento'
                      ? 'before:bg-n-teal-8'
                      : item.key === 'aguardando'
                        ? 'before:bg-n-amber-8'
                        : item.key === 'nao_fechou'
                          ? 'before:bg-n-ruby-8'
                          : 'before:bg-n-green-8'
                "
                @click="openLeadsStatus(item.key)"
              >
                <span
                  class="flex items-center justify-center w-10 h-10 rounded-xl bg-n-alpha-2 text-n-slate-11 shrink-0"
                >
                  <i :class="[item.icon, 'text-lg']" />
                </span>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-sm font-semibold text-n-slate-12">
                      {{ item.title }}
                    </div>
                    <span
                      class="flex items-center justify-center min-w-7 h-7 px-2 text-xs font-semibold rounded-lg bg-n-alpha-2 text-n-slate-11"
                    >
                      <i
                        v-if="isOverviewLoading"
                        class="i-lucide-loader-circle animate-spin"
                      />
                      <template v-else>
                        {{ item.value }}
                      </template>
                    </span>
                  </div>
                  <div class="mt-1 text-xs leading-5 text-n-slate-9">
                    {{ item.description }}
                  </div>
                  <div class="flex items-center gap-1 mt-2 text-[11px] font-medium text-n-slate-8">
                    Ver leads
                    <i class="i-lucide-arrow-right" />
                  </div>
                </div>
              </button>
            </div>
          </section>

          <section class="p-6 border rounded-2xl border-n-weak bg-n-solid-2">
            <h2 class="text-lg font-semibold text-n-slate-12">
              Repescagem
            </h2>

            <p class="mt-1 text-sm text-n-slate-9">
              Organize oportunidades que precisam de uma nova abordagem.
            </p>

            <div class="flex flex-col gap-3 mt-5">
              <button
                type="button"
                class="flex items-center justify-between w-full p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2"
                @click="openRecoveryPeriod('hoje')"
              >
                <div class="flex-1">
                  <div class="text-sm font-semibold text-n-slate-12">
                    Para hoje
                  </div>
                  <div class="mt-1 text-xs text-n-slate-9">
                    Oportunidades programadas para hoje
                  </div>
                </div>
                <div class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center min-w-8 h-8 px-2 text-xs font-semibold rounded-lg bg-n-alpha-2 text-n-slate-11"
                  >
                    <i
                      v-if="isOverviewLoading"
                      class="i-lucide-loader-circle animate-spin"
                    />
                    <template v-else>
                      {{ overviewData.repescagem.hoje }}
                    </template>
                  </span>
                  <i class="text-base i-lucide-chevron-right text-n-slate-8" />
                </div>
              </button>

              <button
                type="button"
                class="flex items-center justify-between w-full p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2"
                @click="openRecoveryPeriod('atrasados')"
              >
                <div class="flex-1">
                  <div class="text-sm font-semibold text-n-slate-12">
                    Atrasados
                  </div>
                  <div class="mt-1 text-xs text-n-slate-9">
                    Oportunidades cuja data de retorno já passou
                  </div>
                </div>
                <div class="flex items-center gap-3">
                  <span
                    class="flex items-center justify-center min-w-8 h-8 px-2 text-xs font-semibold rounded-lg bg-n-alpha-2 text-n-slate-11"
                  >
                    <i
                      v-if="isOverviewLoading"
                      class="i-lucide-loader-circle animate-spin"
                    />
                    <template v-else>
                      {{ overviewData.repescagem.atrasados }}
                    </template>
                  </span>
                  <i class="text-base i-lucide-chevron-right text-n-slate-8" />
                </div>
              </button>
            </div>
          </section>
        </div>
      </template>

      <template v-else-if="activeView === 'leads'">
        <section class="mt-6 border rounded-2xl border-n-weak bg-n-solid-2">
          <div class="p-5 border-b border-n-weak">
            <div
              class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
            >
              <div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-2 mb-3 text-xs font-semibold transition-all border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-10 hover:bg-n-alpha-2 hover:border-n-strong hover:text-n-slate-12"
                  @click="backToOverview"
                >
                  <i class="i-lucide-arrow-left" />
                  Voltar para Visão geral
                </button>

                <div class="flex items-center gap-2">
                  <h2 class="text-lg font-semibold text-n-slate-12">
                    Leads
                  </h2>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-10"
                  >
                    {{ totalCount }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-n-slate-9">
                  Pessoas classificadas pela equipe no funil comercial.
                  <span
                    v-if="activeLeadPeriod !== 'todos'"
                    class="ml-1 font-medium text-n-slate-11"
                  >
                    · {{
                      activeLeadPeriod === 'hoje'
                        ? 'Entradas de hoje'
                        : activeLeadPeriod === 'semana'
                          ? 'Entradas desta semana'
                          : 'Entradas deste mês'
                    }}
                  </span>
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <div
                  class="inline-flex p-1 border rounded-xl border-n-weak bg-n-alpha-1"
                >
                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                    :class="
                      leadsDisplayMode === 'list'
                        ? 'bg-n-background text-n-slate-12 shadow-sm'
                        : 'text-n-slate-9 hover:text-n-slate-12'
                    "
                    @click="leadsDisplayMode = 'list'"
                  >
                    <i class="i-lucide-list" />
                    Lista
                  </button>

                  <button
                    type="button"
                    class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                    :class="
                      leadsDisplayMode === 'kanban'
                        ? 'bg-n-background text-n-slate-12 shadow-sm'
                        : 'text-n-slate-9 hover:text-n-slate-12'
                    "
                    @click="leadsDisplayMode = 'kanban'"
                  >
                    <i class="i-lucide-columns-3" />
                    Kanban
                  </button>
                </div>

                <div
                  v-if="leadsDisplayMode === 'list'"
                  class="flex items-center gap-2 px-3 py-2 border rounded-xl border-n-weak bg-n-background min-w-[280px]"
                >
                  <i class="i-lucide-search text-n-slate-8" />
                <input
                  v-model="searchQuery"
                  type="search"
                  placeholder="Buscar nos leads desta página..."
                  class="w-full text-sm bg-transparent outline-none text-n-slate-12 placeholder:text-n-slate-7"
                />
                </div>
              </div>
            </div>

            <div
              v-if="leadsDisplayMode === 'list'"
              class="flex flex-wrap gap-2 mt-4"
            >
              <button
                v-for="status in statusOptions"
                :key="status.value"
                type="button"
                class="px-3 py-1.5 text-xs font-medium transition-colors border rounded-lg"
                :class="
                  activeStatus === status.value
                    ? 'border-n-strong bg-n-alpha-2 text-n-slate-12'
                    : 'border-n-weak text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12'
                "
                @click="activeStatus = status.value"
              >
                {{ status.label }}
              </button>
            </div>
          </div>

          <div
            v-if="errorMessage"
            class="px-5 py-3 text-sm border-b border-n-weak text-n-ruby-11 bg-n-ruby-2"
          >
            {{ errorMessage }}
          </div>

          <div
            v-if="leadsDisplayMode === 'list' && isLoading"
            class="flex items-center justify-center min-h-[300px]"
          >
            <div class="flex items-center gap-3 text-sm text-n-slate-9">
              <i class="text-lg i-lucide-loader-circle animate-spin" />
              Carregando leads...
            </div>
          </div>

          <div
            v-else-if="leadsDisplayMode === 'list' && !filteredLeads.length"
            class="flex flex-col items-center justify-center min-h-[300px] px-6 text-center"
          >
            <span
              class="flex items-center justify-center w-12 h-12 rounded-2xl bg-n-alpha-2 text-n-slate-11"
            >
              <i class="text-xl i-lucide-user-search" />
            </span>

            <h3 class="mt-4 text-base font-semibold text-n-slate-12">
              Nenhum lead encontrado
            </h3>

            <p class="max-w-md mt-2 text-sm leading-6 text-n-slate-9">
              Um contato aparecerá aqui quando for classificado no funil
              comercial.
            </p>
          </div>

          <div
            v-else-if="leadsDisplayMode === 'list'"
            class="divide-y divide-n-weak"
          >
            <article
              v-for="contact in filteredLeads"
              :key="contact.id"
              class="flex flex-col gap-4 p-5 transition-colors hover:bg-n-alpha-1 lg:flex-row lg:items-center lg:justify-between"
            >
              <div class="flex items-start min-w-0 gap-4">
                <div
                  class="flex items-center justify-center w-11 h-11 text-sm font-semibold rounded-full bg-n-alpha-2 text-n-slate-11 shrink-0"
                >
                  {{
                    (contact.name || '?')
                      .split(' ')
                      .slice(0, 2)
                      .map(part => part[0])
                      .join('')
                      .toUpperCase()
                  }}
                </div>

                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h3
                      class="font-semibold truncate text-n-slate-12 max-w-[420px]"
                    >
                      {{ contact.name || 'Contato sem nome' }}
                    </h3>

                    <span
                      class="px-2 py-0.5 text-[11px] font-semibold rounded-full"
                      :class="
                        statusClass(
                          contact.custom_attributes?.[STATUS_KEY]
                        )
                      "
                    >
                      {{
                        statusLabel(
                          contact.custom_attributes?.[STATUS_KEY]
                        )
                      }}
                    </span>
                  </div>

                  <div class="mt-1 text-sm text-n-slate-9">
                    {{ contactDetail(contact) }}
                  </div>

                  <div
                    class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-n-slate-8"
                  >
                    <span class="flex items-center gap-1.5">
                      <i class="i-lucide-megaphone" />
                      {{
                        originLabel(
                          contact.custom_attributes?.[ORIGIN_KEY]
                        )
                      }}
                    </span>

                    <span class="flex items-center gap-1.5">
                      <i class="i-lucide-calendar-days" />
                      Entrada:
                      {{
                        formatDate(
                          contact.custom_attributes?.[ENTRY_KEY]
                        )
                      }}
                    </span>

                    <span
                      v-if="contact.custom_attributes?.[OWNER_KEY]"
                      class="flex items-center gap-1.5"
                    >
                      <i class="i-lucide-user-round" />
                      {{ contact.custom_attributes[OWNER_KEY] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2 lg:justify-end">
                <button
                  type="button"
                  :disabled="openingConversationContactId === contact.id"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all border rounded-lg shadow-sm border-n-blue-5 bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 hover:border-n-blue-7 hover:shadow disabled:opacity-50"
                  @click="openLatestConversation(contact)"
                >
                  <i
                    :class="
                      openingConversationContactId === contact.id
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-message-circle'
                    "
                  />
                  {{
                    openingConversationContactId === contact.id
                      ? 'Abrindo...'
                      : 'Abrir conversa'
                  }}
                </button>

                <button
                  type="button"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  @click="openContact(contact)"
                >
                  <i class="i-lucide-user-round-search" />
                  Contato
                </button>

                <div class="relative">
                  <select
                    :value="getListSelectedStatus(contact)"
                    :disabled="updatingContactId === contact.id"
                    class="px-3 py-2 pr-8 text-sm font-medium border rounded-lg outline-none appearance-none border-n-weak bg-n-background text-n-slate-11 disabled:opacity-50"
                    @change="
                      listSelectedStatuses[contact.id] = $event.target.value;
                      handleListStatusChange(contact, $event.target.value)
                    "
                  >
                    <option
                      v-for="status in editableStatuses"
                      :key="status.value"
                      :value="status.value"
                    >
                      {{ status.label }}
                    </option>
                  </select>

                  <i
                    class="absolute -translate-y-1/2 pointer-events-none i-lucide-chevron-down right-2 top-1/2 text-n-slate-8"
                  />
                </div>
              </div>
            </article>
          </div>

          <div
            v-if="leadsDisplayMode === 'kanban' && isKanbanLoading"
            class="flex items-center justify-center min-h-[300px]"
          >
            <div class="flex items-center gap-3 text-sm text-n-slate-9">
              <i class="text-lg i-lucide-loader-circle animate-spin" />
              Carregando Kanban...
            </div>
          </div>

          <div
            v-else-if="leadsDisplayMode === 'kanban'"
            class="p-4 overflow-x-auto"
          >
            <div class="grid grid-cols-5 gap-3 min-w-[1180px]">
              <section
                v-for="column in kanbanColumns"
                :key="column.key"
                class="flex flex-col min-w-0 overflow-hidden border rounded-2xl border-n-weak bg-n-alpha-1"
              >
                <div :class="[column.accent, 'h-1 w-full']" />

                <header
                  class="flex items-center justify-between gap-3 px-3 py-3.5 border-b border-n-weak bg-n-background"
                >
                  <div class="flex items-center min-w-0 gap-2.5">
                    <span
                      :class="[
                        column.soft,
                        column.text,
                        'flex items-center justify-center w-8 h-8 rounded-lg shrink-0',
                      ]"
                    >
                      <i :class="[column.icon, 'text-sm']" />
                    </span>

                    <div class="min-w-0">
                      <h3
                        class="text-sm font-semibold truncate text-n-slate-12"
                      >
                        {{ column.label }}
                      </h3>

                      <div class="text-[10px] text-n-slate-8">
                        {{
                          kanbanLists[column.key]?.length === 1
                            ? '1 oportunidade'
                            : `${kanbanLists[column.key]?.length || 0} oportunidades`
                        }}
                      </div>
                    </div>
                  </div>

                  <span
                    :class="[
                      column.soft,
                      column.text,
                      'flex items-center justify-center min-w-7 h-7 px-2 text-xs font-semibold rounded-full',
                    ]"
                  >
                    {{ kanbanLists[column.key]?.length || 0 }}
                  </span>
                </header>

                <Draggable
                  v-model="kanbanLists[column.key]"
                  item-key="id"
                  group="mayara-commercial"
                  class="flex flex-col flex-1 gap-2.5 p-2.5 min-h-[300px]"
                  ghost-class="opacity-40"
                  drag-class="rotate-1"
                  @change="handleKanbanChange(column.key, $event)"
                >
                  <template #item="{ element: contact }">
                    <article
                      class="relative p-3.5 overflow-hidden transition-all border shadow-sm rounded-xl cursor-grab active:cursor-grabbing border-n-weak bg-n-background hover:border-n-strong hover:shadow-md"
                    >
                      <div
                        :class="[
                          column.accent,
                          'absolute top-0 bottom-0 left-0 w-0.5 opacity-70',
                        ]"
                      />

                      <div class="flex items-start gap-3">
                        <div
                          :class="[
                            column.soft,
                            column.text,
                            'flex items-center justify-center w-9 h-9 text-[11px] font-semibold rounded-full shrink-0',
                          ]"
                        >
                          {{ contactInitials(contact) }}
                        </div>

                        <button
                          type="button"
                          class="flex-1 min-w-0 text-left"
                          @click="openContact(contact)"
                        >
                          <div
                            class="text-sm font-semibold truncate text-n-slate-12"
                          >
                            {{ contact.name || 'Contato sem nome' }}
                          </div>

                          <div class="flex items-center gap-1.5 mt-1.5">
                            <span
                              class="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-md bg-n-alpha-2 text-n-slate-9"
                            >
                              <i class="i-lucide-megaphone" />
                              {{
                                originLabel(
                                  contact.custom_attributes?.[ORIGIN_KEY]
                                )
                              }}
                            </span>
                          </div>
                        </button>

                        <button
                          type="button"
                          :disabled="
                            openingConversationContactId === contact.id
                          "
                          class="flex items-center justify-center w-9 h-9 transition-all border rounded-lg shadow-sm shrink-0 border-n-blue-5 bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 hover:border-n-blue-7 hover:shadow disabled:opacity-50"
                          title="Abrir conversa"
                          @click.stop="openLatestConversation(contact)"
                        >
                          <i
                            :class="
                              openingConversationContactId === contact.id
                                ? 'i-lucide-loader-circle animate-spin text-base'
                                : 'i-lucide-message-circle text-base'
                            "
                          />
                        </button>
                      </div>

                      <div
                        class="grid gap-2 pt-3 mt-3 border-t border-n-weak"
                      >
                        <span
                          class="flex items-center gap-2 text-[11px] text-n-slate-8"
                        >
                          <i class="i-lucide-calendar-days shrink-0" />
                          <span class="truncate">
                            Entrada
                            {{
                              formatDate(
                                contact.custom_attributes?.[ENTRY_KEY]
                              )
                            }}
                          </span>
                        </span>

                        <span
                          v-if="contact.custom_attributes?.[OWNER_KEY]"
                          class="flex items-center gap-2 text-[11px] text-n-slate-8"
                        >
                          <i class="i-lucide-user-round shrink-0" />
                          <span class="truncate">
                            {{ contact.custom_attributes[OWNER_KEY] }}
                          </span>
                        </span>

                        <div
                          class="flex items-center justify-end gap-1 pt-1 text-[10px] text-n-slate-7"
                        >
                          <i class="i-lucide-grip-vertical" />
                          Arraste para mover
                        </div>
                      </div>
                    </article>
                  </template>

                  <template #footer>
                    <div
                      v-if="!kanbanLists[column.key]?.length"
                      class="flex flex-col items-center justify-center flex-1 min-h-[190px] px-4 text-center border border-dashed rounded-xl border-n-weak"
                    >
                      <span
                        :class="[
                          column.soft,
                          column.text,
                          'flex items-center justify-center w-9 h-9 rounded-xl',
                        ]"
                      >
                        <i :class="[column.icon, 'text-base']" />
                      </span>

                      <div
                        class="mt-2 text-xs font-medium text-n-slate-9"
                      >
                        Nenhum lead aqui
                      </div>

                      <div class="mt-1 text-[10px] text-n-slate-7">
                        Arraste uma oportunidade para esta etapa
                      </div>
                    </div>
                  </template>
                </Draggable>
              </section>
            </div>
          </div>

          <div
            v-if="
              leadsDisplayMode === 'list' &&
              !isLoading &&
              totalCount > 0
            "
            class="flex items-center justify-between gap-4 px-5 py-4 border-t border-n-weak"
          >
            <div class="text-xs text-n-slate-8">
              Página {{ currentPage }} de {{ totalPages }}
            </div>

            <div class="flex gap-2">
              <button
                type="button"
                :disabled="currentPage <= 1"
                class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-40 disabled:pointer-events-none"
                @click="goToPage(currentPage - 1)"
              >
                Anterior
              </button>

              <button
                type="button"
                :disabled="currentPage >= totalPages"
                class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-40 disabled:pointer-events-none"
                @click="goToPage(currentPage + 1)"
              >
                Próxima
              </button>
            </div>
          </div>
        </section>
      </template>

      <template v-else-if="activeView === 'recovery'">
        <section class="mt-6 overflow-hidden border rounded-2xl border-n-weak bg-n-solid-2">
          <div class="p-5 border-b border-n-weak">
            <div
              class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
            >
              <div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-2 mb-3 text-xs font-semibold transition-all border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-10 hover:bg-n-alpha-2 hover:border-n-strong hover:text-n-slate-12"
                  @click="backToOverview"
                >
                  <i class="i-lucide-arrow-left" />
                  Voltar para Visão geral
                </button>

                <div class="flex items-center gap-2">
                  <h2 class="text-lg font-semibold text-n-slate-12">
                    Repescagem
                  </h2>

                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-10"
                  >
                    {{ recoveryTotalCount }}
                  </span>
                </div>

                <p class="mt-1 text-sm text-n-slate-9">
                  Renovações, reativações e novas abordagens programadas pela equipe.
                </p>
              </div>

              <div class="flex flex-col gap-3 xl:items-end">
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="period in recoveryPeriods"
                    :key="period.value"
                    type="button"
                    class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg"
                    :class="
                      recoveryPeriod === period.value
                        ? 'border-n-strong bg-n-alpha-2 text-n-slate-12'
                        : 'border-n-weak text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12'
                    "
                    @click="recoveryPeriod = period.value"
                  >
                    {{ period.label }}
                  </button>
                </div>

                <div class="flex flex-wrap gap-2">
                  <label class="flex items-center gap-2">
                    <span class="text-xs font-medium text-n-slate-8">
                      Tipo
                    </span>

                    <select
                      v-model="recoveryType"
                      class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11 hover:border-n-strong focus:border-n-strong"
                    >
                      <option
                        v-for="type in recoveryTypes"
                        :key="type.value"
                        :value="type.value"
                      >
                        {{ type.label }}
                      </option>
                    </select>
                  </label>

                  <label class="flex items-center gap-2">
                    <span class="text-xs font-medium text-n-slate-8">
                      Ordenar
                    </span>

                    <select
                      v-model="recoverySort"
                      class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11 hover:border-n-strong focus:border-n-strong"
                    >
                      <option
                        v-for="option in recoverySortOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="errorMessage"
            class="px-5 py-3 text-sm border-b border-n-weak text-n-ruby-11 bg-n-ruby-2"
          >
            {{ errorMessage }}
          </div>

          <div
            v-if="isRecoveryLoading"
            class="flex items-center justify-center min-h-[300px]"
          >
            <div class="flex items-center gap-3 text-sm text-n-slate-9">
              <i class="text-lg i-lucide-loader-circle animate-spin" />
              Carregando repescagem...
            </div>
          </div>

          <div
            v-else-if="!recoveryContacts.length"
            class="flex flex-col items-center justify-center min-h-[300px] px-6 text-center"
          >
            <span
              class="flex items-center justify-center w-12 h-12 rounded-2xl bg-n-alpha-2 text-n-slate-11"
            >
              <i class="text-xl i-lucide-calendar-check" />
            </span>

            <h3 class="mt-4 text-base font-semibold text-n-slate-12">
              Nenhuma oportunidade nesta fila
            </h3>

            <p class="max-w-md mt-2 text-sm leading-6 text-n-slate-9">
              Quando uma nova abordagem for programada, ela aparecerá aqui
              automaticamente na data correspondente.
            </p>
          </div>

          <div v-else class="divide-y divide-n-weak">
            <article
              v-for="contact in recoveryContacts"
              :key="contact.id"
              class="flex flex-col gap-4 p-5 transition-colors hover:bg-n-alpha-1 xl:flex-row xl:items-center xl:justify-between"
            >
              <div class="flex items-start min-w-0 gap-4">
                <div
                  class="flex items-center justify-center w-11 h-11 text-sm font-semibold rounded-full bg-n-alpha-2 text-n-slate-11 shrink-0"
                >
                  {{ contactInitials(contact) }}
                </div>

                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <h3
                      class="font-semibold truncate text-n-slate-12 max-w-[420px]"
                    >
                      {{ contact.name || 'Contato sem nome' }}
                    </h3>

                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-n-alpha-2 text-n-slate-10"
                    >
                      <span class="font-normal opacity-70">Motivo:</span>
                      {{
                        recoveryTypeLabel(
                          contact.custom_attributes?.mayara_tipo_repescagem
                        )
                      }}
                    </span>

                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full"
                      :class="
                        statusClass(
                          contact.custom_attributes?.[STATUS_KEY]
                        )
                      "
                    >
                      <span class="font-normal opacity-70">Status:</span>
                      {{
                        statusLabel(
                          contact.custom_attributes?.[STATUS_KEY]
                        )
                      }}
                    </span>
                  </div>

                  <div class="mt-1 text-sm text-n-slate-9">
                    {{ contactDetail(contact) }}
                  </div>

                  <div
                    class="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-n-slate-8"
                  >
                    <span
                      class="flex items-center gap-1.5 font-medium text-n-slate-11"
                    >
                      <i class="i-lucide-calendar-clock" />
                      Próxima abordagem:
                      {{
                        formatDate(
                          contact.custom_attributes?.mayara_proxima_repescagem
                        )
                      }}
                    </span>

                    <span class="flex items-center gap-1.5">
                      <i class="i-lucide-megaphone" />
                      {{
                        originLabel(
                          contact.custom_attributes?.[ORIGIN_KEY]
                        )
                      }}
                    </span>

                    <span
                      v-if="contact.custom_attributes?.[OWNER_KEY]"
                      class="flex items-center gap-1.5"
                    >
                      <i class="i-lucide-user-round" />
                      {{ contact.custom_attributes[OWNER_KEY] }}
                    </span>
                  </div>

                  <div
                    v-if="contact.custom_attributes?.mayara_motivo_nao_fechamento"
                    class="mt-2 text-xs leading-5 text-n-slate-9"
                  >
                    Motivo:
                    {{ contact.custom_attributes.mayara_motivo_nao_fechamento }}
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2 xl:justify-end">
                <button
                  v-if="canStartRenewal(contact)"
                  type="button"
                  :disabled="startingRenewalContactId !== null"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all border rounded-lg shadow-sm border-n-green-5 bg-n-green-3 text-n-green-11 hover:bg-n-green-4 hover:border-n-green-7 hover:shadow disabled:opacity-50"
                  @click="startRenewal(contact)"
                >
                  <i
                    :class="
                      startingRenewalContactId === contact.id
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-refresh-cw'
                    "
                  />
                  {{
                    startingRenewalContactId === contact.id
                      ? 'Iniciando...'
                      : 'Iniciar renovação'
                  }}
                </button>

                <button
                  type="button"
                  :disabled="openingConversationContactId === contact.id"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all border rounded-lg shadow-sm border-n-blue-5 bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 hover:border-n-blue-7 hover:shadow disabled:opacity-50"
                  @click="openLatestConversation(contact)"
                >
                  <i
                    :class="
                      openingConversationContactId === contact.id
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-message-circle'
                    "
                  />
                  {{
                    openingConversationContactId === contact.id
                      ? 'Abrindo...'
                      : 'Abrir conversa'
                  }}
                </button>

                <button
                  type="button"
                  class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  @click="openContact(contact)"
                >
                  <i class="i-lucide-user-round-search" />
                  Contato
                </button>
              </div>
            </article>
          </div>

          <div
            v-if="
              !isRecoveryLoading &&
              recoveryTotalCount > 0
            "
            class="flex items-center justify-between gap-4 px-5 py-4 border-t border-n-weak"
          >
            <div class="text-xs text-n-slate-8">
              Página {{ recoveryCurrentPage }} de {{ recoveryTotalPages }}
            </div>

            <div class="flex gap-2">
              <button
                type="button"
                :disabled="recoveryCurrentPage <= 1"
                class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-40 disabled:pointer-events-none"
                @click="goToRecoveryPage(recoveryCurrentPage - 1)"
              >
                Anterior
              </button>

              <button
                type="button"
                :disabled="recoveryCurrentPage >= recoveryTotalPages"
                class="px-3 py-2 text-xs font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-40 disabled:pointer-events-none"
                @click="goToRecoveryPage(recoveryCurrentPage + 1)"
              >
                Próxima
              </button>
            </div>
          </div>
        </section>
      </template>

      <template v-else-if="activeView === 'closures'">
        <div class="mt-6 space-y-5">
          <!-- Hero / executive summary -->
          <section
            class="relative overflow-hidden border rounded-2xl border-n-weak bg-n-solid-2"
          >
            <div
              class="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-n-green-8 via-n-teal-8 to-n-blue-8"
            />

            <div class="p-6 lg:p-7">
              <div
                class="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between"
              >
                <div class="max-w-2xl">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 px-3 py-2 mb-3 text-xs font-semibold transition-all border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-10 hover:bg-n-alpha-2 hover:border-n-strong hover:text-n-slate-12"
                    @click="backToOverview"
                  >
                    <i class="i-lucide-arrow-left" />
                    Voltar para Visão geral
                  </button>

                  <div
                    class="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-n-green-10"
                  >
                    <i class="text-sm i-lucide-chart-no-axes-combined" />
                    Relatório comercial
                  </div>

                  <h2
                    class="mt-2 text-2xl font-semibold tracking-tight text-n-slate-12"
                  >
                    Fechamentos
                  </h2>

                  <p class="mt-2 text-sm leading-6 text-n-slate-9">
                    Confira as conversões registradas pela equipe e use este
                    extrato para conciliar os fechamentos com o financeiro.
                  </p>
                </div>

                <div
                  class="flex items-center self-start gap-1 p-1 border rounded-xl border-n-weak bg-n-alpha-1"
                >
                  <button
                    type="button"
                    title="Mês anterior"
                    class="flex items-center justify-center w-9 h-9 transition-colors rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2"
                    @click="changeClosuresMonth(-1)"
                  >
                    <i class="i-lucide-chevron-left" />
                  </button>

                  <div
                    class="min-w-[170px] px-3 text-center text-sm font-semibold text-n-slate-12"
                  >
                    {{ closuresMonthLabel }}
                  </div>

                  <button
                    type="button"
                    title="Próximo mês"
                    class="flex items-center justify-center w-9 h-9 transition-colors rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2"
                    @click="changeClosuresMonth(1)"
                  >
                    <i class="i-lucide-chevron-right" />
                  </button>
                </div>
              </div>

              <div class="grid gap-4 mt-7 lg:grid-cols-[0.8fr_2.2fr]">
                <div
                  class="p-5 border rounded-2xl border-n-green-5 bg-n-green-2"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <div class="text-sm font-medium text-n-green-11">
                        Fechamentos no período
                      </div>

                      <div
                        class="mt-2 text-4xl font-semibold tracking-tight text-n-slate-12"
                      >
                        <i
                          v-if="isClosuresLoading"
                          class="text-2xl i-lucide-loader-circle animate-spin text-n-green-10"
                        />
                        <template v-else>
                          {{ closuresTotalCount }}
                        </template>
                      </div>
                    </div>

                    <span
                      class="flex items-center justify-center w-11 h-11 rounded-xl bg-n-green-4 text-n-green-11"
                    >
                      <i class="text-xl i-lucide-badge-check" />
                    </span>
                  </div>

                  <div class="mt-4 text-xs leading-5 text-n-green-11">
                    Cada fechamento comercial registrado no período,
                    incluindo renovações concluídas.
                  </div>
                </div>

                <div class="p-5 border rounded-2xl border-n-weak bg-n-alpha-1">
                  <div class="flex items-center justify-between gap-4">
                    <div>
                      <div class="text-sm font-semibold text-n-slate-12">
                        Por responsável
                      </div>
                      <div class="mt-1 text-xs text-n-slate-8">
                        Distribuição dos fechamentos registrados no período.
                      </div>
                    </div>

                    <i class="text-lg i-lucide-users-round text-n-slate-8" />
                  </div>

                  <div
                    v-if="isClosuresLoading"
                    class="flex items-center h-20 text-sm text-n-slate-8"
                  >
                    <i class="mr-2 i-lucide-loader-circle animate-spin" />
                    Carregando equipe...
                  </div>

                  <div
                    v-else-if="closuresResponsibleCards.length"
                    class="grid gap-3 mt-4 sm:grid-cols-2 xl:grid-cols-3"
                  >
                    <div
                      v-for="owner in closuresResponsibleCards"
                      :key="owner.name"
                      class="flex items-center justify-between gap-3 p-3 border rounded-xl border-n-weak bg-n-background"
                    >
                      <div class="flex items-center min-w-0 gap-3">
                        <span
                          class="flex items-center justify-center w-8 h-8 text-xs font-semibold rounded-full bg-n-alpha-2 text-n-slate-11 shrink-0"
                        >
                          {{
                            owner.name
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map(part => part[0])
                              .join('')
                              .toUpperCase()
                          }}
                        </span>

                        <span
                          class="text-sm font-medium truncate text-n-slate-11"
                        >
                          {{ owner.name }}
                        </span>
                      </div>

                      <span
                        class="flex items-center justify-center min-w-8 h-8 px-2 text-xs font-semibold rounded-lg bg-n-green-3 text-n-green-11"
                      >
                        {{ owner.count }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-else
                    class="flex items-center h-20 text-sm text-n-slate-8"
                  >
                    Nenhum responsável para este período.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Detailed commercial extract -->
          <section
            class="overflow-hidden border rounded-2xl border-n-weak bg-n-solid-2"
          >
            <div
              class="flex flex-col gap-4 p-5 border-b border-n-weak lg:flex-row lg:items-center lg:justify-between"
            >
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    Extrato de fechamentos
                  </h3>

                  <span
                    class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-green-3 text-n-green-11"
                  >
                    {{ closuresTotalCount }}
                  </span>
                </div>

                <p class="mt-1 text-sm text-n-slate-9">
                  Quem fechou, quando fechou e a origem de cada oportunidade.
                </p>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <label
                  class="flex items-center gap-2 px-3 py-2 border rounded-xl border-n-weak bg-n-background"
                >
                  <i class="text-sm i-lucide-arrow-up-down text-n-slate-8" />
                  <span class="text-xs font-medium text-n-slate-8">
                    Ordenar
                  </span>

                  <select
                    v-model="closuresSort"
                    class="text-xs font-semibold bg-transparent outline-none text-n-slate-11"
                  >
                    <option
                      v-for="option in closuresSortOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <button
                  type="button"
                  :disabled="!closuresTotalCount || isExportingClosures"
                  class="flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors border rounded-xl border-n-weak bg-n-background text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-40 disabled:cursor-not-allowed"
                  @click="exportClosuresCsv"
                >
                  <i
                    :class="
                      isExportingClosures
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-download'
                    "
                  />
                  {{ isExportingClosures ? 'Exportando...' : 'Exportar CSV' }}
                </button>
              </div>
            </div>

            <div
              v-if="closuresErrorMessage"
              class="px-5 py-3 text-sm border-b border-n-weak text-n-ruby-11 bg-n-ruby-2"
            >
              {{ closuresErrorMessage }}
            </div>

            <div
              v-if="isClosuresLoading"
              class="flex items-center justify-center min-h-[320px]"
            >
              <div class="flex items-center gap-3 text-sm text-n-slate-9">
                <i class="text-lg i-lucide-loader-circle animate-spin" />
                Carregando fechamentos...
              </div>
            </div>

            <div
              v-else-if="!closuresContacts.length"
              class="flex flex-col items-center justify-center min-h-[320px] px-6 text-center"
            >
              <span
                class="flex items-center justify-center w-12 h-12 rounded-2xl bg-n-green-3 text-n-green-11"
              >
                <i class="text-xl i-lucide-calendar-check-2" />
              </span>

              <h3 class="mt-4 text-base font-semibold text-n-slate-12">
                Nenhum fechamento em {{ closuresMonthLabel }}
              </h3>

              <p class="max-w-md mt-2 text-sm leading-6 text-n-slate-9">
                Quando um lead for marcado como fechado neste período, ele
                aparecerá automaticamente neste relatório.
              </p>
            </div>

            <div v-else class="divide-y divide-n-weak">
              <article
                v-for="contact in closuresContacts"
                :key="contact.closure_event?.id || contact.id"
                class="flex flex-col gap-4 p-5 transition-colors hover:bg-n-alpha-1 xl:flex-row xl:items-center xl:justify-between"
              >
                <div class="flex items-start min-w-0 gap-4">
                  <div
                    class="flex items-center justify-center w-11 h-11 text-sm font-semibold rounded-full bg-n-green-3 text-n-green-11 shrink-0"
                  >
                    {{ contactInitials(contact) }}
                  </div>

                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h3
                        class="font-semibold truncate text-n-slate-12 max-w-[430px]"
                      >
                        {{ contact.name || 'Contato sem nome' }}
                      </h3>

                      <span
                        class="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-n-green-3 text-n-green-11"
                      >
                        <i class="i-lucide-circle-check" />
                        Fechado
                      </span>
                    </div>

                    <div class="mt-1 text-sm text-n-slate-9">
                      {{ contactDetail(contact) }}
                    </div>

                    <div
                      class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-xs text-n-slate-8"
                    >
                      <span
                        class="flex items-center gap-1.5 font-medium text-n-slate-11"
                      >
                        <i class="i-lucide-calendar-check" />
                        {{
                          formatDate(contact.closure_event?.occurred_on)
                        }}
                      </span>

                      <span class="flex items-center gap-1.5">
                        <i class="i-lucide-user-round-check" />
                        {{
                          contact.closure_event?.responsible ||
                          'Responsável não informado'
                        }}
                      </span>

                      <span class="flex items-center gap-1.5">
                        <i class="i-lucide-megaphone" />
                        {{
                          originLabel(contact.closure_event?.origin)
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex flex-wrap items-center gap-2 xl:justify-end">
                  <button
                    type="button"
                    :disabled="openingConversationContactId === contact.id"
                    class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all border rounded-lg shadow-sm border-n-green-5 bg-n-green-3 text-n-green-11 hover:bg-n-green-4 hover:border-n-green-7 disabled:opacity-50"
                    @click="openLatestConversation(contact)"
                  >
                    <i
                      :class="
                        openingConversationContactId === contact.id
                          ? 'i-lucide-loader-circle animate-spin'
                          : 'i-lucide-message-circle'
                      "
                    />
                    {{
                      openingConversationContactId === contact.id
                        ? 'Abrindo...'
                        : 'Abrir conversa'
                    }}
                  </button>

                  <button
                    type="button"
                    class="flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                    @click="openContact(contact)"
                  >
                    <i class="i-lucide-user-round-search" />
                    Contato
                  </button>
                </div>
              </article>
            </div>

            <div
              v-if="!isClosuresLoading && closuresTotalCount > 0"
              class="flex items-center justify-between gap-4 px-5 py-4 border-t border-n-weak"
            >
              <div class="text-xs text-n-slate-8">
                Página {{ closuresCurrentPage }} de {{ closuresTotalPages }}
              </div>

              <div class="flex gap-2">
                <button
                  type="button"
                  :disabled="closuresCurrentPage <= 1"
                  class="px-3 py-2 text-xs font-medium border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 disabled:opacity-40"
                  @click="goToClosuresPage(closuresCurrentPage - 1)"
                >
                  Anterior
                </button>

                <button
                  type="button"
                  :disabled="closuresCurrentPage >= closuresTotalPages"
                  class="px-3 py-2 text-xs font-medium border rounded-lg border-n-weak text-n-slate-10 hover:bg-n-alpha-1 disabled:opacity-40"
                  @click="goToClosuresPage(closuresCurrentPage + 1)"
                >
                  Próxima
                </button>
              </div>
            </div>
          </section>

          <div
            class="flex items-start gap-3 px-4 py-3 text-xs leading-5 border rounded-xl border-n-weak bg-n-alpha-1 text-n-slate-8"
          >
            <i class="mt-0.5 i-lucide-info shrink-0" />
            <span>
              Este relatório representa registros comerciais da Mayara Care.
              Ele apoia a conferência com o financeiro, mas não representa
              faturamento ou prontuário clínico.
            </span>
          </div>
        </div>
      </template>
    </div>

    <Dialog
      ref="notClosedDialogRef"
      type="edit"
      width="md"
      title="Marcar como não fechou"
      description="Registre o motivo e, se fizer sentido, programe uma nova abordagem."
      confirm-button-label="Confirmar não fechamento"
      cancel-button-label="Cancelar"
      :disable-confirm-button="notClosedFormIsInvalid"
      :is-loading="Boolean(updatingContactId)"
      @confirm="confirmNotClosed"
      @close="handleNotClosedDialogClose"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Motivo do não fechamento
          </label>

          <textarea
            v-model="notClosedReason"
            rows="3"
            maxlength="500"
            placeholder="Ex.: achou o valor alto, decidiu aguardar, não respondeu..."
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-y border-n-weak bg-n-background text-n-slate-11"
          />

          <p class="mb-0 text-xs text-n-slate-8">
            Este campo é obrigatório.
          </p>
        </div>

        <label
          class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak bg-n-alpha-1"
        >
          <input
            v-model="scheduleNotClosedRecovery"
            type="checkbox"
            class="mt-0.5"
          />

          <div>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              Programar nova abordagem
            </p>

            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-9">
              O lead aparecerá na fila de Repescagem na data escolhida.
            </p>
          </div>
        </label>

        <template v-if="scheduleNotClosedRecovery">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              Quando tentar novamente?
            </label>

            <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <button
                v-for="option in notClosedRecoveryOptions"
                :key="option.value"
                type="button"
                class="px-3 py-2 text-xs font-medium transition-all border rounded-lg"
                :class="
                  notClosedRecoveryMode === option.value
                    ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                    : 'border-n-weak bg-n-background text-n-slate-10 hover:bg-n-alpha-2'
                "
                @click="notClosedRecoveryMode = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div
            v-if="notClosedRecoveryMode === 'custom'"
            class="flex flex-col gap-2"
          >
            <label class="text-sm font-medium text-n-slate-12">
              Data da nova abordagem
            </label>

            <input
              v-model="customNotClosedRecoveryDate"
              type="date"
              :min="addDays(1)"
              class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11"
            />
          </div>

          <div
            v-if="
              notClosedRecoveryDate &&
              !notClosedRecoveryDateIsInvalid
            "
            class="flex items-start gap-3 p-3 rounded-lg bg-n-blue-3 text-n-blue-11"
          >
            <i class="i-lucide-calendar-check mt-0.5 shrink-0" />

            <div>
              <p class="mb-0 text-sm font-medium">
                Nova abordagem programada
              </p>

              <p class="mb-0 text-xs">
                Este lead voltará para a equipe em
                {{ formattedNotClosedRecoveryDate }}.
              </p>
            </div>
          </div>
        </template>

        <p class="mb-0 text-xs leading-5 text-n-slate-9">
          O lead ficará com status Não fechou. Sem uma nova abordagem
          programada, ele não aparecerá na fila de Repescagem.
        </p>
      </div>
    </Dialog>

    <Dialog
      ref="closingDialogRef"
      type="edit"
      width="md"
      title="Marcar como fechado"
      description="A conversão será registrada e o próximo contato para renovação precisa ser programado."
      confirm-button-label="Confirmar fechamento"
      cancel-button-label="Cancelar"
      :disable-confirm-button="closingDateIsInvalid"
      :is-loading="Boolean(updatingContactId)"
      @confirm="confirmClosing"
      @close="handleClosingDialogClose"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Quando devemos entrar em contato novamente?
          </label>

          <div class="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <button
              v-for="option in closingOptions"
              :key="option.value"
              type="button"
              class="px-3 py-2 text-xs font-medium transition-all border rounded-lg"
              :class="
                closingMode === option.value
                  ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak bg-n-background text-n-slate-10 hover:bg-n-alpha-2'
              "
              @click="closingMode = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div v-if="closingMode === 'custom'" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Data do próximo contato
          </label>

          <input
            v-model="customClosingDate"
            type="date"
            :min="addDays(1)"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-11"
          />
        </div>

        <div
          v-if="closingNextContactDate && !closingDateIsInvalid"
          class="flex items-start gap-3 p-3 rounded-lg bg-n-blue-3 text-n-blue-11"
        >
          <i class="i-lucide-calendar-check mt-0.5 shrink-0" />

          <div>
            <p class="mb-0 text-sm font-medium">
              Renovação programada
            </p>

            <p class="mb-0 text-xs">
              A equipe deverá procurar este contato em
              {{ formattedClosingDate }}.
            </p>
          </div>
        </div>

        <p class="mb-0 text-xs leading-5 text-n-slate-9">
          O lead continuará com status Fechado. Esta data será utilizada
          pela fila de Repescagem/Renovação da Mayara Care.
        </p>
      </div>
    </Dialog>
  </div>
</template>
